import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Search } from "lucide-react";

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("All");
  const [query, setQuery] = useState("");

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => {
    const set = new Set(products.map((p) => p.category));
    return ["All", ...Array.from(set)];
  }, [products]);

  const filtered = useMemo(() => {
    return products.filter((p) => {
      const catOk = category === "All" || p.category === category;
      const q = query.trim().toLowerCase();
      const qOk = !q || p.name.toLowerCase().includes(q) || p.short_description.toLowerCase().includes(q);
      return catOk && qOk;
    });
  }, [products, category, query]);

  return (
    <div data-testid="shop-page">
      <section className="bg-oshveda-bgAlt border-b border-oshveda-borderSubtle">
        <div className="container-x py-16 md:py-20">
          <div className="label-eyebrow mb-3">All products</div>
          <h1 className="font-serif text-5xl md:text-6xl tracking-tight text-oshveda-ink leading-tight">
            The Oshveda <em className="text-oshveda-accent not-italic">apothecary</em>
          </h1>
          <p className="text-oshveda-ink2 text-lg mt-4 max-w-2xl">
            A curated collection of pure, authentic Ayurvedic remedies — for body, mind and skin.
          </p>
        </div>
      </section>

      <section className="py-10 sticky top-20 z-30 bg-oshveda-bg/95 backdrop-blur-md border-b border-oshveda-borderSubtle">
        <div className="container-x flex flex-col md:flex-row gap-4 items-stretch md:items-center justify-between">
          <div className="flex flex-wrap gap-2" data-testid="category-filters">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                data-testid={`category-filter-${c.toLowerCase().replace(/\s/g, "-")}`}
                className={`px-5 py-2 rounded-full text-xs tracking-widest uppercase border transition-all ${
                  category === c
                    ? "bg-oshveda-primary text-oshveda-bg border-oshveda-primary"
                    : "bg-transparent text-oshveda-ink2 border-oshveda-borderStrong hover:border-oshveda-primary"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="relative md:max-w-xs w-full">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-oshveda-muted" />
            <input
              type="text"
              placeholder="Search remedies…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="search-input"
              className="w-full pl-11 pr-4 py-2.5 bg-white border border-oshveda-borderSubtle rounded-full text-sm focus:outline-none focus:border-oshveda-primary transition-colors"
            />
          </div>
        </div>
      </section>

      <section className="section-y bg-oshveda-bg">
        <div className="container-x">
          {loading ? (
            <div className="text-center text-oshveda-muted py-20" data-testid="shop-loading">Loading collection…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center text-oshveda-muted py-20" data-testid="shop-empty">No products found.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="shop-grid">
              {filtered.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
